// src/pages/VerifyEmailChange.js
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';

const VerifyEmailChange = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const hasVerifiedRef = useRef(false);
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  useEffect(() => {
    if (hasVerifiedRef.current || status !== 'verifying') {
      return;
    }
    
    hasVerifiedRef.current = true;

    const verifyEmailChange = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Verification token is missing. Please check your email and use the complete verification link.');
        showError('Verification token is missing');
        return;
      }

      try {
        const response = await api.post(`/api/profile/me/verify-email-change/${token}`);
        
        setStatus('success');
        setMessage('Email changed successfully! Your new email address is now active.');
        showSuccess('Email changed successfully!');
        
        // Update auth if token is provided
        if (response.data.user) {
          // Refresh user data
          const userResponse = await api.get('/api/profile/me');
          setAuth(localStorage.getItem('token'), userResponse.data.user);
        }
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/profile');
        }, 3000);
      } catch (err) {
        setStatus('error');
        let errorMsg = 'Email change verification failed';
        if (err.response?.data?.error) {
          if (typeof err.response.data.error === 'string') {
            errorMsg = err.response.data.error;
          } else if (err.response.data.error.message) {
            errorMsg = err.response.data.error.message;
          }
        } else if (err.message) {
          errorMsg = err.message;
        }
        setMessage(errorMsg);
        showError(errorMsg);
      }
    };

    verifyEmailChange();
  }, [token, navigate, showSuccess, showError, status]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-3xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-8">
          Email Change Verification
        </h2>

        {status === 'verifying' && (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-shayek-primary mb-4"></div>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700">
              Verifying your new email address...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-green-800 dark:text-green-300 mb-2">
              Email Changed Successfully!
            </h3>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700 mb-4">
              {message}
            </p>
            <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
              Redirecting you to your profile...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-red-800 dark:text-red-300 mb-2">
              Verification Failed
            </h3>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700 mb-4">
              {message}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/profile')}
                className="w-full bg-shayek-light-200 dark:bg-shayek-dark-200 text-shayek-light-900 dark:text-shayek-dark-900 py-2 px-4 rounded-lg hover:bg-shayek-light-300 dark:hover:bg-shayek-dark-300 focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors"
              >
                Back to Profile
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailChange;

